import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatINR, formatDate, BRAND } from "@/lib/api";
import Navbar from "@/components/quickwash/Navbar";
import Footer from "@/components/quickwash/Footer";
import { CheckCircle2, Circle, Phone, MessageCircle } from "lucide-react";

const STEPS = ["Received", "Processing", "Dry Cleaning", "Ironing", "Ready", "Out for Delivery", "Delivered"];

const PublicBill = () => {
  const { ref } = useParams(); // bill_number or id
  const [bill, setBill] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/bills/${ref}`)
      .then((r) => setBill(r.data))
      .catch(() => setNotFound(true));
  }, [ref]);

  if (notFound) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen flex flex-col">
        <Navbar />
        <div className="qw-container flex-1 py-24 text-center">
          <div className="max-w-md mx-auto qw-card p-10">
            <div className="text-lg font-semibold text-slate-900">Bill not found</div>
            <p className="text-sm text-slate-500 mt-2">The bill reference "{ref}" doesn't exist. Please check the number.</p>
            <Link to="/track" className="qw-btn-primary mt-6 inline-flex" data-testid="publicbill-track">Track by Mobile</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  if (!bill) return <div className="p-10 text-slate-500">Loading…</div>;

  const currentIdx = STEPS.indexOf(bill.status);
  const waLink = `https://wa.me/${BRAND.waPhone}?text=${encodeURIComponent(`Hi Quick Wash, I have a question about bill ${bill.bill_number}`)}`;

  return (
    <div className="bg-[#F8FAFC] min-h-screen flex flex-col">
      <Navbar />
      <div className="qw-container py-10 lg:py-14 flex-1" data-testid="public-bill-page">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div className="qw-card p-8">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-widest">Your Bill</div>
                <div className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }} data-testid="public-bill-number">{bill.bill_number}</div>
                <div className="text-sm text-slate-500 mt-1">Order date {formatDate(bill.order_date)} · Delivery {formatDate(bill.delivery_date)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-widest">Amount</div>
                <div className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Poppins' }}>{formatINR(bill.grand_total)}</div>
                <div className="text-xs text-slate-500 mt-1">Paid {formatINR(bill.advance)} · Balance {formatINR(bill.balance)}</div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase text-slate-500 font-semibold">Customer</div>
                <div className="font-semibold text-slate-900">{bill.customer_name}</div>
                <div className="text-slate-600">{bill.mobile}</div>
              </div>
              <div className="sm:text-right">
                <div className="text-xs uppercase text-slate-500 font-semibold">Status</div>
                <span className="qw-badge-blue mt-1">{bill.status}</span>
              </div>
            </div>
          </div>

          {/* Items - transparent rate breakdown */}
          <div className="qw-card p-6 md:p-8" data-testid="public-items">
            <div className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>Item-wise Breakdown</div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <th className="text-left py-3 px-2">Item</th>
                    <th className="text-left py-3 px-2">Service</th>
                    <th className="text-right py-3 px-2">Qty / Wt</th>
                    <th className="text-right py-3 px-2">Rate</th>
                    <th className="text-right py-3 px-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.items.map((it, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-3 px-2 font-medium text-slate-900">{it.item}</td>
                      <td className="py-3 px-2 text-slate-600">{it.service}</td>
                      <td className="py-3 px-2 text-right">
                        {it.unit === "kg" ? (
                          <><div>{it.weight_kg} kg</div><div className="text-[11px] text-slate-500">{it.quantity} pcs</div></>
                        ) : (
                          <>{it.quantity} pcs</>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">{formatINR(it.rate)}{it.unit === "kg" ? "/kg" : ""}</td>
                      <td className="py-3 px-2 text-right font-semibold">{formatINR(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end">
              <div className="w-full md:w-80 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{formatINR(bill.subtotal)}</span></div>
                {bill.gst_percent > 0 && (
                  <div className="flex justify-between text-slate-700" data-testid="public-gst">
                    <span>GST @ {bill.gst_percent}%</span><span>+ {formatINR(bill.gst_amount)}</span>
                  </div>
                )}
                {bill.discount > 0 && (
                  <div className="flex justify-between text-emerald-600 font-semibold" data-testid="public-discount">
                    <span>Discount</span><span>− {formatINR(bill.discount)}</span>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-3 flex justify-between text-base"><span className="font-bold text-slate-900">Grand Total</span><span className="font-bold text-blue-600">{formatINR(bill.grand_total)}</span></div>
                <div className="flex justify-between text-slate-500"><span>Advance ({bill.payment_mode})</span><span>{formatINR(bill.advance)}</span></div>
                <div className="flex justify-between font-semibold"><span className="text-slate-900">Balance Due</span><span>{formatINR(bill.balance)}</span></div>
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          <div className="qw-card p-6 md:p-8">
            <div className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }}>Order Progress</div>
            <div className="space-y-3">
              {STEPS.map((s, idx) => {
                const done = idx <= currentIdx;
                return (
                  <div key={s} className="flex items-center gap-3">
                    {done ? <CheckCircle2 size={20} className="text-emerald-500"/> : <Circle size={20} className="text-slate-300"/>}
                    <span className={done ? "text-slate-900 font-medium" : "text-slate-400"}>{s}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Help */}
          <div className="qw-card p-6 md:p-8 flex flex-wrap gap-3 items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>Need help with this bill?</div>
              <p className="text-sm text-slate-500">Call {BRAND.phone} / {BRAND.phone2} or WhatsApp us.</p>
            </div>
            <div className="flex gap-2">
              <a href={`tel:${BRAND.phone}`} className="qw-btn-outline" data-testid="public-call"><Phone size={16}/>Call</a>
              <a href={waLink} target="_blank" rel="noreferrer" className="qw-btn-wa" data-testid="public-wa"><MessageCircle size={16}/>WhatsApp</a>
            </div>
          </div>

          {/* Terms */}
          <div className="qw-card p-5 text-xs text-slate-500 italic leading-relaxed" data-testid="public-terms">
            <span className="font-semibold not-italic text-slate-700">Terms &amp; Conditions:</span> {BRAND.terms}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PublicBill;
